import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { CloudOff, Loader2, LogIn, RefreshCw, Users } from "lucide-react";
import AppLiveClock from "../components/ui/AppLiveClock";
import ReferralsStatusHero from "../components/referrals/ReferralsStatusHero";
import ReferralListRow from "../components/referrals/ReferralListRow";
import { patchAppUser, selectAppUser } from "../redux/appAuthSlice";
import {
  fetchMyReferrals,
  claimReferralReward,
  claimFirstDepositReward,
} from "../redux/referralSlice";
import "../referrals.css";

const Referrals = () => {
  const dispatch = useDispatch();
  const user = useSelector(selectAppUser);
  const userId = user?._id;
  const {
    rewardAmount,
    unlockScoreThreshold,
    firstDepositReward,
    referrals,
    totals,
    pagination,
    loading,
    loadingMore,
    claimingReferralId,
    claimingFirstDeposit,
    error,
  } = useSelector((s) => s.referrals);

  const [claimNotice, setClaimNotice] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(
    (options = {}) => dispatch(fetchMyReferrals({ page: 1, limit: 20, ...options })),
    [dispatch]
  );

  useEffect(() => {
    if (userId) load();
  }, [userId, load]);

  useEffect(() => {
    if (!claimNotice) return undefined;
    const timer = window.setTimeout(() => setClaimNotice(null), 4000);
    return () => window.clearTimeout(timer);
  }, [claimNotice]);

  const onRefresh = async () => {
    if (refreshing || loading) return;
    setRefreshing(true);
    try {
      await load();
    } finally {
      setRefreshing(false);
    }
  };

  const onLoadMore = () => {
    if (loadingMore || loading) return;
    if (pagination.page >= pagination.totalPages) return;
    dispatch(
      fetchMyReferrals({
        page: pagination.page + 1,
        limit: pagination.limit,
        append: true,
      })
    );
  };

  const applyClaimedCash = (cashBalance) => {
    if (cashBalance == null) return;
    dispatch(patchAppUser({ CASH: cashBalance }));
  };

  const handleClaimReferral = async (item) => {
    if (!item?.id || !item.canClaim || claimingReferralId) return;
    const result = await dispatch(claimReferralReward(item.id));
    if (claimReferralReward.fulfilled.match(result)) {
      applyClaimedCash(result.payload.cashBalance);
      setClaimNotice({
        tone: "success",
        message: result.payload.message || "Referral reward claimed.",
      });
      return;
    }
    setClaimNotice({
      tone: "error",
      message: String(result.payload || "Failed to claim referral reward"),
    });
  };

  const handleClaimFirstDeposit = async () => {
    if (!firstDepositReward?.canClaim || claimingFirstDeposit) return;
    const result = await dispatch(claimFirstDepositReward());
    if (claimFirstDepositReward.fulfilled.match(result)) {
      applyClaimedCash(result.payload.cashBalance);
      setClaimNotice({
        tone: "success",
        message: result.payload.message || "First deposit bonus claimed.",
      });
      return;
    }
    setClaimNotice({
      tone: "error",
      message: String(result.payload || "Failed to claim first deposit bonus"),
    });
  };

  if (!userId) {
    return (
      <div className="ref-page">
        <div className="ref-state">
          <LogIn size={32} />
          <h1>Sign in to view referrals</h1>
          <Link to="/app/login" className="ref-primary-btn">
            Go to login
          </Link>
        </div>
      </div>
    );
  }

  if (loading && !referrals.length) {
    return (
      <div className="ref-page">
        <div className="ref-state">
          <Loader2 size={22} className="animate-spin" />
          <p>Loading referrals…</p>
        </div>
      </div>
    );
  }

  if (error && !referrals.length) {
    return (
      <div className="ref-page">
        <div className="ref-state">
          <CloudOff size={32} />
          <p>{String(error)}</p>
          <button type="button" className="ref-primary-btn" onClick={() => load()}>
            <RefreshCw size={13} />
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ref-page">
      <div className="ref-desk">
        <header className="ref-chrome">
          <div className="ref-chrome-lead">
            <h1 className="ref-chrome-title">Referrals</h1>
          </div>
          <div className="ref-chrome-clock-wrap app-chrome-clock-center">
            <AppLiveClock className="app-chrome-clock" />
          </div>
          <div className="ref-chrome-actions">
            <button
              type="button"
              className="ref-chrome-btn"
              onClick={onRefresh}
              disabled={refreshing || loading}
              aria-label="Refresh referrals"
              title="Refresh"
            >
              <RefreshCw
                size={14}
                strokeWidth={2.3}
                className={refreshing || loading ? "animate-spin" : ""}
              />
            </button>
          </div>
        </header>

        <div className="ref-desk-body">
          <ReferralsStatusHero
            rewardAmount={rewardAmount}
            totals={totals}
            firstDepositReward={firstDepositReward}
            claimingFirstDeposit={claimingFirstDeposit}
            onClaimFirstDeposit={handleClaimFirstDeposit}
          />

          {claimNotice ? (
            <p
              className={`ref-hero-notice${
                claimNotice.tone === "error" ? " ref-hero-notice-error" : ""
              }`}
              role="status"
            >
              {claimNotice.message}
            </p>
          ) : null}

          <section className="ref-list" aria-label="Referred friends">
            <header className="ref-list-head">
              <h2>Referred friends</h2>
              <span className="ref-list-count tabular-nums">
                {pagination.total ?? totals?.totalReferrals ?? referrals.length}
              </span>
            </header>

            <div className="ref-list-scroll">
              {referrals.length ? (
                <div className="ref-list-items">
                  {referrals.map((item) => (
                    <ReferralListRow
                      key={item.id}
                      item={item}
                      unlockScoreThreshold={unlockScoreThreshold}
                      rewardAmount={rewardAmount}
                      claiming={String(claimingReferralId) === String(item.id)}
                      onClaim={handleClaimReferral}
                    />
                  ))}
                </div>
              ) : (
                <div className="ref-list-empty">
                  <Users size={22} strokeWidth={1.25} aria-hidden />
                  <p>No referrals yet.</p>
                </div>
              )}

              {pagination.page < pagination.totalPages ? (
                <div className="ref-load-more">
                  <button
                    type="button"
                    className="ref-load-more-btn"
                    onClick={onLoadMore}
                    disabled={loadingMore}
                  >
                    {loadingMore ? (
                      <>
                        <Loader2 size={13} className="animate-spin" />
                        Loading…
                      </>
                    ) : (
                      "Load more"
                    )}
                  </button>
                </div>
              ) : null}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Referrals;
